'use client';

import type { FragmentType } from '~/shared/codegen/fragment-masking';
import { useFragment } from '~/shared/codegen/fragment-masking';
import ContentPlaceholder from '~/shared/components/content-placeholder';
import { PopularCourseItemFragment } from '~/shared/components/header/components/popular-courses/helpers/popular-courses-cache';
import Link from '~/shared/components/link';
import { PAGE_NAMES } from '~/shared/constants';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { getUrl } from '~/shared/helpers/string-helpers';
import type { I18nextOptionsType } from '~/shared/types/i18n-type';

import './popular-course-item.scss';

import noImage from '~/assets/no-image.webp';

type PopularCourseItemProps = Readonly<{
  data: FragmentType<typeof PopularCourseItemFragment> | null;
  pageToGo?: string;
  isContainItemImage?: boolean;
  pageTagsToGo?: string;
  t: (key: string, options?: I18nextOptionsType) => string;
  isLoading: boolean;
  isError: boolean;
  lng: string;
  onClick?: () => void;
}>;

const PopularCourseItem = ({
  data,
  isError,
  isLoading,
  t,
  lng,
  onClick,
  isContainItemImage = false,
}: PopularCourseItemProps) => {
  const course = useFragment(PopularCourseItemFragment, data);
  const href = getLocaleLink(
    getUrl({
      link: PAGE_NAMES.COURSE,
      alias: course?.alias,
    }),
    lng,
  );

  return (
    <div className="popular-course-item">
      <div className="popular-course-item__top-block">
        <ContentPlaceholder
          className="popular-course-item__image"
          isLoading={isLoading}
          isError={isError}
          content={
            <div
              className="popular-course-item__image"
              style={{
                background: `url(${
                  course?.thumbnail?.path ?? noImage.src
                }) center/${isContainItemImage ? 'contain' : 'cover'} no-repeat`,
              }}
            />
          }
        />

        <ContentPlaceholder
          className="popular-course-item__title"
          isLoading={isLoading}
          isError={isError}
          content={
            <Link itemProp="url" className="popular-course-item__title" onClick={onClick} href={href}>
              {course?.title ?? ''}
            </Link>
          }
        />
      </div>

      <div className="popular-course-item__link">
        <Link className="link--underlined" itemProp="url" onClick={onClick} href={href}>
          {t('link.go-to-course')}
        </Link>
      </div>
    </div>
  );
};

export default PopularCourseItem;
